import React, { useState, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import '../../assets/css/mydashboard.css';
import * as moment from "moment";

const StoreDashboard = ({ selectedUtid, storeDashboardData }) => {

  const [openSections, setOpenSections] = useState({
    // Expand all sections by default on page load
    transaction: true,
    brand: true,
    genieCash: true,
    support: true,
    salesVisits: true,
    last3Visits: true,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const monthLabels = useMemo(() => {
    // Use months from API if available, otherwise calculate fallback
    if (storeDashboardData?.months && Array.isArray(storeDashboardData.months) && storeDashboardData.months.length >= 3) {
      return {
        current: storeDashboardData.months[0] || "",
        prev: storeDashboardData.months[1] || "",
        prevPrev: storeDashboardData.months[2] || "",
      };
    }

    // Fallback calculation if API months not available yet
    const options = { month: "short" };
    const today = new Date();
    const year = today.getFullYear().toString().slice(-2);

    const current =
      today.toLocaleString("en-US", options) + "-" + year;

    const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const prevYear = prevMonth.getFullYear().toString().slice(-2);
    const prev =
      prevMonth.toLocaleString("en-US", options) + "-" + prevYear;

    const prevPrevMonth = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    const prevPrevYear = prevPrevMonth.getFullYear().toString().slice(-2);
    const prevPrev =
      prevPrevMonth.toLocaleString("en-US", options) + "-" + prevPrevYear;

    return {
      current: current,
      prev: prev,
      prevPrev: prevPrev,
    };
  }, [storeDashboardData?.months]);

  if (!storeDashboardData) {
    return (
      <Row className="mt-4">
        <Col md={12}>
          <div className="content-tdetails-table table-full-width">
            <p>Loading...</p>
          </div>
        </Col>
      </Row>
    );
  }

  const formatTargetValue = (value) => (value === 0 ? "-" : value ?? "-");

  const {
    transactionDashboard,
    brandDashboard,
    genieCash,
    support,
    salesVisits,
    last3Visits,
    monthlyTargets,
  } = storeDashboardData;

  return (
    <Row>
      <Col md={12}>
        <div className="content-tdetails-table table-full-width">
          {/* Table 1: Volume & Count by Parameter */}
          <div className="mb-4">
            <div className="table-responsive">
              <table className="table table-bordered table-hover store-dashboard-table">
                <thead className="table-light">
                  <tr>
                    <th rowSpan="2">Parameter</th>
                    <th rowSpan="2" className="text-end">Target</th>
                    <th colSpan="2">{monthLabels.current}</th>
                    <th colSpan="2">{monthLabels.prev}</th>
                    <th colSpan="2">{monthLabels.prevPrev}</th>
                  </tr>
                  <tr>
                    <th className="text-end">
                      Volume
                      <br />
                      (Rs. Lakhs)
                    </th>
                    <th className="text-end">Count</th>
                    <th className="text-end">
                      Volume
                      <br />
                      (Rs. Lakhs)
                    </th>
                    <th className="text-end">Count</th>
                    <th className="text-end">
                      Volume
                      <br />
                      (Rs. Lakhs)
                    </th>
                    <th className="text-end">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Transaction Dashboard Section */}
                  <tr
                    onClick={() => toggleSection("transaction")}
                    className="collapsible-row"
                  >
                    <td>
                      {openSections.transaction ? "▼" : "▶"} Transaction Dashboard
                    </td>
                    <td colSpan="7"></td>
                  </tr>
                  {openSections.transaction && (
                    <>
                      {transactionDashboard && transactionDashboard.length > 0 ? (
                        transactionDashboard.map((row, i) => {
                          // Show target only for "Total" row
                          const showTarget = row.parameter === "Total";
                          const targetValue = showTarget
                            ? formatTargetValue(monthlyTargets?.currentMonth?.totalGtvTarget)
                            : "-";
                          return (
                            <tr key={`tx-${i}`} className="detail-row">
                              <td className="detail-cell">{row.parameter}</td>
                              <td className="text-end">{targetValue}</td>
                              <td className="text-end">{row.currentMonthVol}</td>
                              <td className="text-end">{row.currentMonthCount}</td>
                              <td className="text-end">{row.previousMonthVol}</td>
                              <td className="text-end">{row.previousMonthCount}</td>
                              <td className="text-end">{row.previousToPreviousVol}</td>
                              <td className="text-end">{row.previousToPreviousCount}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr className="detail-row">
                          <td colSpan="8" className="text-center detail-cell">
                            No data available
                          </td>
                        </tr>
                      )}
                    </>
                  )}

                  {/* Brand Dashboard Section */}
                  <tr
                    onClick={() => toggleSection("brand")}
                    className="collapsible-row"
                  >
                    <td>
                      {openSections.brand ? "▼" : "▶"} Brand Dashboard
                    </td>
                    <td colSpan="7"></td>
                  </tr>
                  {openSections.brand && (
                    <>
                      {brandDashboard && brandDashboard.length > 0 ? (
                        brandDashboard.map((row, i) => (
                          <tr key={`brand-${i}`} className="detail-row">
                            <td className="detail-cell">{row.parameter}</td>
                            <td className="text-end">-</td>
                            <td className="text-end">{row.currentMonthVol}</td>
                            <td className="text-end">{row.currentMonthCount}</td>
                            <td className="text-end">{row.previousMonthVol}</td>
                            <td className="text-end">{row.previousMonthCount}</td>
                            <td className="text-end">{row.previousToPreviousVol}</td>
                            <td className="text-end">{row.previousToPreviousCount}</td>
                          </tr>
                        ))
                      ) : (
                        <tr className="detail-row">
                          <td colSpan="8" className="text-center detail-cell">
                            No data available
                          </td>
                        </tr>
                      )}
                    </>
                  )}

                  {/* Genie Cash Section */}
                  <tr
                    onClick={() => toggleSection("genieCash")}
                    className="collapsible-row"
                  >
                    <td>
                      {openSections.genieCash ? "▼" : "▶"} Genie Cash
                    </td>
                    <td colSpan="7"></td>
                  </tr>
                  {openSections.genieCash && (
                    <tr className="detail-row">
                      <td className="detail-cell">Genie Cash</td>
                      <td className="text-end">
                        {(() => {
                          const resolvedTarget =
                            genieCash?.currentMonthTarget != null
                              ? genieCash.currentMonthTarget
                              : monthlyTargets?.currentMonth?.genieCashTarget;
                          return formatTargetValue(resolvedTarget);
                        })()}
                      </td>
                      <td className="text-end">{genieCash?.currentMonthVol ?? "-"}</td>
                      <td className="text-end">{genieCash?.currentMonthCount ?? "-"}</td>
                      <td className="text-end">{genieCash?.previousMonthVol ?? "-"}</td>
                      <td className="text-end">{genieCash?.previousMonthCount ?? "-"}</td>
                      <td className="text-end">{genieCash?.previousToPreviousVol ?? "-"}</td>
                      <td className="text-end">{genieCash?.previousToPreviousCount ?? "-"}</td>
                    </tr>
                  )}

                  {/* Support Section */}
                  <tr
                    onClick={() => toggleSection("support")}
                    className="collapsible-row"
                  >
                    <td>
                      {openSections.support ? "▼" : "▶"} Support
                    </td>
                    <td colSpan="7"></td>
                  </tr>
                      {openSections.support && (
                      <>
                        <tr className="detail-row">
                          <td className="detail-cell">Support tickets - Total</td>
                          <td className="text-end">-</td>
                          <td className="text-end">-</td>
                          <td className="text-end">{support?.currentMonth?.totalTickets ?? "-"}</td>
                          <td className="text-end">-</td>
                          <td className="text-end">{support?.previousMonth?.totalTickets ?? "-"}</td>
                          <td className="text-end">-</td>
                          <td className="text-end">{support?.previousPreviousMonth?.totalTickets ?? "-"}</td>
                        </tr>
                        <tr className="detail-row">
                          <td className="detail-cell">Support tickets - Closed within TAT</td>
                          <td className="text-end">-</td>
                          <td className="text-end">-</td>
                          <td className="text-end">{support?.currentMonth?.closedWithinTAT ?? "-"}</td>
                          <td className="text-end">-</td>
                          <td className="text-end">{support?.previousMonth?.closedWithinTAT ?? "-"}</td>
                          <td className="text-end">-</td>
                          <td className="text-end">{support?.previousPreviousMonth?.closedWithinTAT ?? "-"}</td>
                        </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Last 3 Visits Table */}
          {last3Visits && (
            <>
              <div className="dashboard-separator" style={{ margin: '20px 0' }}></div>
              <div className="mb-4">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover store-dashboard-table">
                    <thead className="table-light">
                      <tr>
                        <th>Parameter</th>
                        <th>Visit 1</th>
                        <th>Visit 2</th>
                        <th>Visit 3</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Last 3 Visits Section */}
                      <tr
                        onClick={() => toggleSection("last3Visits")}
                        className="collapsible-row"
                      >
                        <td>
                          {openSections.last3Visits ? "▼" : "▶"} Last 3 Visits
                        </td>
                        <td colSpan="3"></td>
                      </tr>
                      {openSections.last3Visits && (
                        <>
                          {/* Get last 3 visits - handle both array format and month-split format */}
                          {(() => {
                            // Handle both structures: direct array or split by month
                            let visits = [];
                            if (Array.isArray(last3Visits)) {
                              // If it's a direct array
                              visits = last3Visits;
                            } else if (last3Visits?.currentMonth && Array.isArray(last3Visits.currentMonth)) {
                              // If it's split by month, use current month
                              visits = last3Visits.currentMonth;
                            }

                            // Ensure we have exactly 3 visits (pad with empty if needed)
                            const paddedVisits = [...visits, null, null, null].slice(0, 3);

                            return (
                              <>
                                {/* Name Row */}
                                <tr className="detail-row">
                                  <td className="detail-cell">Name</td>
                                  {paddedVisits.map((visit, idx) => (
                                    <td key={`name-${idx}`} className="text-end">
                                      {visit && visit.person_name ? (
                                        <>
                                          {visit.person_name}
                                        </>
                                      ) : "-"}
                                    </td>
                                  ))}
                                </tr>
                                {/* Date Row */}
                                <tr className="detail-row">
                                  <td className="detail-cell">Date</td>
                                  {paddedVisits.map((visit, idx) => (
                                    <td key={`date-${idx}`} className="text-end">
                                      {visit && visit.visit_datetime
                                        ? moment(visit.visit_datetime).format("DD MMM YY, HH:mm")
                                        : "-"}
                                    </td>
                                  ))}
                                </tr>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default StoreDashboard;